import { useState } from "react";
import type { ReportRange } from "@ticketnjoy/types";

import { ISTANBUL_TZ, type Format } from "@/lib/format";

export const REPORT_RANGES: readonly ReportRange[] = ["today", "yesterday", "week", "month", "custom"];

const ISTANBUL_DAY = new Intl.DateTimeFormat("en-US", {
  timeZone: ISTANBUL_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** İstanbul'a göre bugün, `<input type="date">` biçiminde (`yyyy-MM-dd`). */
export function istanbulTodayIso(): string {
  const parts = ISTANBUL_DAY.formatToParts(new Date());
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function shiftIsoDay(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** `yyyy-MM-dd` saatsiz gün; UTC'de biçimlenir ki tarayıcı saat dilimi günü kaydırmasın. */
export function formatIsoDay(fmt: Format, iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return fmt.date(date, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
}

/** İki ucu dahil gün aralığı; tek günse tek tarih. */
export function formatIsoRange(fmt: Format, from: string, to: string): string {
  return from === to ? formatIsoDay(fmt, from) : `${formatIsoDay(fmt, from)} – ${formatIsoDay(fmt, to)}`;
}

/**
 * Hazır aralık + tarih aralığı alanları. Hazır aralıkların sınırlarını backend
 * İstanbul takvimiyle çözer; `from`/`to` yalnız `custom`'da gönderilir.
 */
export function useReportRange(initial: ReportRange) {
  const [range, setRange] = useState<ReportRange>(initial);
  const [to, setTo] = useState(istanbulTodayIso);
  const [from, setFrom] = useState(() => shiftIsoDay(istanbulTodayIso(), -29));
  const valid = range !== "custom" || (from !== "" && to !== "" && from <= to);
  return {
    range,
    setRange,
    from,
    setFrom,
    to,
    setTo,
    valid,
    /** Sorgu ve önbellek anahtarı için: custom dışında tarih taşımaz. */
    query: {
      range,
      from: range === "custom" ? from : null,
      to: range === "custom" ? to : null,
    },
  };
}

export type ReportRangeQuery = ReturnType<typeof useReportRange>["query"];

export function appendRangeParams(params: URLSearchParams, q: ReportRangeQuery): URLSearchParams {
  params.set("range", q.range);
  if (q.from) params.set("from", q.from);
  if (q.to) params.set("to", q.to);
  return params;
}
